import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FileText,
  Download,
  ArrowLeft,
  TrendingUp,
  DollarSign,
  Clock,
  Target,
  AlertTriangle,
  Lightbulb,
  BarChart3,
  PieChart,
  Shield,
  CheckCircle2,
  Loader2,
  FileSpreadsheet,
  Presentation,
  FileType,
  Settings2,
  Sparkles,
  BookOpen,
  RefreshCw
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { formatCurrency, getCurrencySymbol } from '../lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Color palette for charts
const COLORS = ['#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899', '#14b8a6', '#f97316'];

// Helper function to clean up section text by removing markdown artifacts
const cleanSectionText = (text) => {
  if (!text) return '';
  return text
    .replace(/^#+\s*/gm, '')
    .replace(/^\s*##\s*$/gm, '')
    .replace(/^(?:EXECUTIVE SUMMARY|TECHNICAL BREAKDOWN|METHODOLOGY|RISK ANALYSIS|RISK CONSIDERATIONS|STRATEGIC RECOMMENDATIONS|RECOMMENDATIONS)[:\s]*/gim, '')
    .replace(/^\s*[a-z]\s*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// Helper function to format text with basic markdown-like styling
import { SafeHTML } from '../lib/sanitize';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHeroCompact } from '../components/layout/PageHeroCompact';
import { getLightSectionTheme } from '../lib/sectionThemes';

const formatNarrativeText = (text) => {
  if (!text) return '';
  return text
    .replace(/^#+\s*(.*)$/gm, '$1')
    .replace(/^\s*#{1,6}\s*$/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br />');
};

// Parse narrative into sections for better display
const parseNarrativeSections = (text) => {
  if (!text) return null;
  
  const sections = {
    executive: '',
    methodology: '',
    recommendations: '',
    risks: ''
  };
  
  const execMatch = text.match(/(?:#+\s*)?(?:EXECUTIVE SUMMARY|Executive Summary)[:\s]*([\s\S]*?)(?=(?:#+\s*)?(?:METHODOLOGY|TECHNICAL|CAUSAL|RISK|RECOMMENDATION|$))/i);
  const methodMatch = text.match(/(?:#+\s*)?(?:METHODOLOGY|TECHNICAL BREAKDOWN|CAUSAL COMPONENTS|TECHNICAL)[:\s]*([\s\S]*?)(?=(?:#+\s*)?(?:RISK|RECOMMENDATION|STRATEGIC|$))/i);
  const riskMatch = text.match(/(?:#+\s*)?(?:RISK|RISKS|RISK ANALYSIS|RISK CONSIDERATIONS)[:\s]*([\s\S]*?)(?=(?:#+\s*)?(?:RECOMMENDATION|STRATEGIC|NEXT STEPS|$))/i);
  const recoMatch = text.match(/(?:#+\s*)?(?:RECOMMENDATION|STRATEGIC|NEXT STEPS|STRATEGIC RECOMMENDATIONS)[:\s]*([\s\S]*?)$/i);
  
  if (execMatch) sections.executive = cleanSectionText(execMatch[1]);
  if (methodMatch) sections.methodology = cleanSectionText(methodMatch[1]);
  if (riskMatch) sections.risks = cleanSectionText(riskMatch[1]);
  if (recoMatch) sections.recommendations = cleanSectionText(recoMatch[1]);
  
  if (!sections.executive && !sections.methodology) {
    sections.executive = cleanSectionText(text);
  }
  
  return sections;
};

// Component to render narrative sections
const NarrativeSections = ({ narrative }) => {
  const sections = parseNarrativeSections(narrative);
  if (!sections) return null;

  return (
    <div className="space-y-6">
      {/* Executive Summary */}
      {sections.executive && (
        <div className="p-5 bg-white rounded-lg border border-purple-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-purple-600" />
            <h4 className="font-semibold text-slate-800 text-base">Executive Summary</h4>
          </div>
          <SafeHTML 
            className="text-sm text-slate-600 prose prose-sm max-w-none leading-relaxed"
            html={formatNarrativeText(sections.executive)}
          />
        </div>
      )}

      {/* Technical/Methodology Breakdown */}
      {sections.methodology && (
        <div className="p-5 bg-white rounded-lg border border-blue-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            <h4 className="font-semibold text-slate-800 text-base">Technical Breakdown</h4>
          </div>
          <SafeHTML 
            className="text-sm text-slate-600 prose prose-sm max-w-none leading-relaxed"
            html={formatNarrativeText(sections.methodology)}
          />
        </div>
      )}

      {/* Risk Analysis */}
      {sections.risks && (
        <div className="p-5 bg-white rounded-lg border border-amber-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h4 className="font-semibold text-slate-800 text-base">Risk Analysis</h4>
          </div>
          <SafeHTML 
            className="text-sm text-slate-600 prose prose-sm max-w-none leading-relaxed"
            html={formatNarrativeText(sections.risks)}
          />
        </div>
      )}

      {/* Recommendations */}
      {sections.recommendations && (
        <div className="p-5 bg-white rounded-lg border border-emerald-100 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="h-5 w-5 text-emerald-600" />
            <h4 className="font-semibold text-slate-800 text-base">Strategic Recommendations</h4>
          </div>
          <SafeHTML 
            className="text-sm text-slate-600 prose prose-sm max-w-none leading-relaxed"
            html={formatNarrativeText(sections.recommendations)}
          />
        </div>
      )}
    </div>
  );
};

const BoardReadyPage = () => {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currency } = useApp();
  
  const [scenario, setScenario] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [contentLevel, setContentLevel] = useState('concise'); // 'concise' or 'comprehensive'
  const [includeNarrative, setIncludeNarrative] = useState(true);
  const [userLogo, setUserLogo] = useState(null);
  const [isGeneratingNarrative, setIsGeneratingNarrative] = useState(false);
  const [localNarrative, setLocalNarrative] = useState(null); // For newly generated narratives

  // Fetch scenario data
  useEffect(() => {
    const fetchScenario = async () => {
      try {
        const response = await api.get(`/scenarios/${scenarioId}`);
        setScenario(response.data);
        
        // Fetch user's company logo for exports
        try {
          const logoRes = await api.get('/auth/logo');
          if (logoRes.data.logo) {
            setUserLogo(logoRes.data.logo);
          }
        } catch (e) {
          // No custom logo - that's fine

        }
      } catch (error) {
        logger.error('Error fetching scenario:', error);
        toast.error('Failed to load scenario');
        navigate('/saved-scenarios');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (scenarioId) {
      fetchScenario();
    }
  }, [scenarioId, navigate]);

  // Get effective narrative (either from scenario or locally generated)
  const effectiveNarrative = localNarrative || scenario?.narrative;

  // Generate narrative directly from Board Ready page
  const handleGenerateNarrative = async () => {
    if (!scenario) return;
    
    setIsGeneratingNarrative(true);
    try {
      const config = scenario.configuration || {};
      const results = scenario.results || {};
      
      // Determine measure and KPI names based on analysis type
      let measureName = 'Selected Measure';
      let kpiNames = 'Selected KPIs';
      
      if (scenario.analysis_type === 'single') {
        measureName = config.lei_measure_id || 'Selected Measure';
        kpiNames = config.kpi_ids?.join(', ') || 'Selected KPIs';
      } else if (scenario.analysis_type === 'portfolio') {
        const enabledMeasures = config.measures?.filter(m => m.enabled)?.length || 0;
        measureName = `${enabledMeasures} LEI Measures (Portfolio)`;
        kpiNames = config.kpi_id || 'Selected KPI';
      } else if (scenario.analysis_type === 'matrix') {
        const enabledMeasures = config.measures?.filter(m => m.enabled)?.length || 0;
        measureName = `${enabledMeasures} LEI Measures (Full Matrix)`;
        kpiNames = `${config.kpi_ids?.length || 10} Business KPIs`;
      }
      
      const response = await api.post('/generate-narrative', {
        lei_measure_name: measureName,
        kpi_name: kpiNames,
        results: results,
        configuration: config,
        analysis_type: scenario.analysis_type || 'single',
        include_sections: true,
      });
      
      const generatedNarrative = response.data.narrative;
      setLocalNarrative(generatedNarrative);
      
      // Save the narrative to the scenario
      try {
        await api.patch(`/scenarios/${scenarioId}`, {
          narrative: generatedNarrative
        });
        // Update local scenario state
        setScenario(prev => ({ ...prev, narrative: generatedNarrative }));
        toast.success('Narrative generated and saved!');
      } catch (saveError) {
        logger.error('Failed to save narrative:', saveError);
        toast.success('Narrative generated (save failed - you can still view it)');
      }
    } catch (error) {
      logger.error('Narrative generation error:', error);
      toast.error('Failed to generate narrative. Please try again.');
    } finally {
      setIsGeneratingNarrative(false);
    }
  };

  // Export handlers
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const response = await api.post(
        '/export/board-ready',
        {
          scenario_id: scenarioId,
          scenario: scenario,
          format: format,
          content_level: contentLevel,
          include_narrative: includeNarrative,
          custom_logo: userLogo,
          currency: currency
        },
        { responseType: 'blob' }
      );
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 
              format === 'pptx' ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation' :
              format === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' :
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Board_Ready_${scenario?.name?.replace(/\s+/g, '_') || 'Report'}_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`${format.toUpperCase()} exported successfully`);
    } catch (error) {
      logger.error('Export error:', error);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Helper functions
  const getROIColor = (roi) => {
    if (roi >= 200) return 'text-emerald-600';
    if (roi >= 100) return 'text-teal-600';
    if (roi >= 50) return 'text-amber-600';
    return 'text-rose-600';
  };

  const getROIAssessment = (roi) => {
    if (roi >= 300) return { label: 'Exceptional', color: 'bg-emerald-100 text-emerald-700' };
    if (roi >= 200) return { label: 'Strong', color: 'bg-teal-100 text-teal-700' };
    if (roi >= 100) return { label: 'Good', color: 'bg-blue-100 text-blue-700' };
    if (roi >= 50) return { label: 'Moderate', color: 'bg-amber-100 text-amber-700' };
    return { label: 'Cautious', color: 'bg-rose-100 text-rose-700' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!scenario) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Scenario Not Found</h2>
        <p className="text-slate-500 mb-4">The requested scenario could not be loaded.</p>
        <Button onClick={() => navigate('/saved-scenarios')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Scenarios
        </Button>
      </div>
    );
  }

  const results = scenario.results || {};
  const config = scenario.configuration || {};
  const roi = results.total_roi_percent || results.roi_percent || 0;
  const roiAssessment = getROIAssessment(roi);
  const financialImpact = results.total_financial_impact || results.financial_impact || 0;
  const netBenefit = results.total_net_benefit || results.net_benefit || 0;
  const kpiDelta = results.total_kpi_delta_percent || results.kpi_delta_percent || 0;
  const paybackMonths = results.payback_months || 0;
  const interventionCost = config.intervention_cost || 0;
  const probPositiveROI = results.prob_positive_roi || 0;

  // Prepare chart data
  const measureData = results.measure_results || results.measure_impacts || [];
  const kpiData = results.kpi_results || results.kpi_impacts || [];

  const measureChartData = measureData.slice(0, 8).map((m, i) => ({
    name: m.lei_measure_name || m.name || `Measure ${i + 1}`,
    impact: m.financial_impact || m.total_financial_impact || 0,
    kpiDelta: m.kpi_delta_percent || m.total_kpi_delta_percent || 0
  }));

  const investmentVsReturnData = [
    { name: 'Investment', value: interventionCost, fill: '#ef4444' },
    { name: 'Net Return', value: Math.max(netBenefit, 0), fill: '#10b981' }
  ];

  const __pageTheme = getLightSectionTheme('ImpactModelling');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHeroCompact title={`Board Ready View`} description={`One-page board-grade summary of a scenario.`} theme={__pageTheme} />
    )}>
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
                  </div>
        
        {/* Export Controls */}
        <div className="flex items-center gap-3">
          {/* Content Level Toggle */}
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
            <Label htmlFor="content-level" className="text-sm text-slate-600">Detail:</Label>
            <Select value={contentLevel} onValueChange={setContentLevel}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="concise">Concise (3-5 pages)</SelectItem>
                <SelectItem value="comprehensive">Full (8-12 pages)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Export Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" disabled={isExporting}>
                {isExporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Export Format</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleExport('pdf')}>
                <FileText className="mr-2 h-4 w-4 text-rose-500" />
                PDF Document
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('pptx')}>
                <Presentation className="mr-2 h-4 w-4 text-orange-500" />
                PowerPoint
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('docx')}>
                <FileType className="mr-2 h-4 w-4 text-blue-500" />
                Word Document
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                <FileSpreadsheet className="mr-2 h-4 w-4 text-emerald-500" />
                Excel Spreadsheet
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="include-narrative" className="text-xs">Include AI Narrative</Label>
                  <Switch
                    id="include-narrative"
                    checked={includeNarrative}
                    onCheckedChange={setIncludeNarrative}
                  />
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Branding Notice */}
      {!userLogo && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Settings2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            <strong>Tip:</strong> Add your company logo in{' '}
            <Link to="/admin" className="underline hover:text-blue-900">Organization Settings</Link>{' '}
            to customize exported documents with your branding.
          </p>
        </div>
      )}

      {/* Page 1: Executive Summary */}
      <Card className="border-2 border-teal-200 shadow-lg" data-testid="executive-summary-card">
        <CardHeader className="bg-gradient-to-r from-teal-50 to-emerald-50 border-b">
          <div className="flex items-center justify-between">
            <div>
              <Badge className="mb-2 bg-teal-100 text-teal-700">Executive Summary</Badge>
              <CardTitle className="text-xl">{scenario.name || 'Learning Investment Analysis'}</CardTitle>
              <CardDescription>{formatDate(scenario.created_at)}</CardDescription>
            </div>
            <Badge className={`text-lg px-4 py-2 ${roiAssessment.color}`}>
              {roiAssessment.label} Investment
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Key Headline */}
            <div className="space-y-4">
              <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl border border-teal-200">
                <p className="text-sm text-slate-600 mb-1">Return on Investment</p>
                <p className={`text-5xl font-bold ${getROIColor(roi)}`}>
                  {(roi / 100).toFixed(1)}x
                </p>
                <p className="text-sm text-slate-500 mt-1">{roi.toFixed(0)}% ROI</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Investment</p>
                  <p className="text-lg font-bold text-rose-600">{formatCurrency(interventionCost, currency)}</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Net Return</p>
                  <p className="text-lg font-bold text-emerald-600">{formatCurrency(netBenefit, currency)}</p>
                </div>
              </div>
            </div>
            
            {/* Right: Investment vs Return Chart */}
            <div>
              <p className="text-sm font-medium text-slate-700 mb-3 text-center">Investment vs. Return</p>
              <ResponsiveContainer width="100%" height={200}>
                <RechartsPie>
                  <Pie
                    data={investmentVsReturnData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${formatCurrency(value, currency)}`}
                  >
                    {investmentVsReturnData.map((entry, index) => (
                      <Cell key={`cell-${entry.name || index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value, currency)} />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          </div>
          
          {/* Narrative Summary */}
          {scenario.narrative && (
            <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-800 leading-relaxed">
                {scenario.narrative.split('\n')[0]?.replace(/^##\s*EXECUTIVE SUMMARY\s*/i, '').substring(0, 300)}
                {scenario.narrative.length > 300 && '...'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Page 2: Key Metrics Dashboard */}
      <Card data-testid="metrics-dashboard-card">
        <CardHeader>
          <Badge className="w-fit mb-2 bg-blue-100 text-blue-700">Key Metrics</Badge>
          <CardTitle className="text-lg">Performance Dashboard</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
              <TrendingUp className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500">ROI Multiplier</p>
              <p className="text-2xl font-bold text-emerald-600">{(roi / 100).toFixed(1)}x</p>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
              <DollarSign className="h-6 w-6 text-blue-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500">Net Benefit</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(netBenefit, currency)}</p>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200">
              <Clock className="h-6 w-6 text-purple-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500">Payback Period</p>
              <p className="text-2xl font-bold text-purple-600">{paybackMonths.toFixed(1)} mo</p>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <Target className="h-6 w-6 text-amber-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500">KPI Uplift</p>
              <p className="text-2xl font-bold text-amber-600">{kpiDelta.toFixed(2)}%</p>
            </div>
            
            <div className="text-center p-4 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border border-teal-200">
              <CheckCircle2 className="h-6 w-6 text-teal-600 mx-auto mb-2" />
              <p className="text-xs text-slate-500">Success Probability</p>
              <p className="text-2xl font-bold text-teal-600">{probPositiveROI.toFixed(0)}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Page 3: Impact Visualization */}
      {measureChartData.length > 0 && (
        <Card data-testid="impact-visualization-card">
          <CardHeader>
            <Badge className="w-fit mb-2 bg-purple-100 text-purple-700">Impact Analysis</Badge>
            <CardTitle className="text-lg">Financial Impact by Measure</CardTitle>
            <CardDescription>Contribution of each LEI measure to total financial impact</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={measureChartData} layout="vertical" margin={{ left: 120 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => formatCurrency(v, currency)} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => formatCurrency(value, currency)} />
                <Bar dataKey="impact" fill="#10b981" radius={[0, 4, 4, 0]}>
                  {measureChartData.map((entry, index) => (
                    <Cell key={`cell-${entry.name || index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Page 4: Risk & Assumptions */}
      <Card data-testid="risk-assumptions-card">
        <CardHeader>
          <Badge className="w-fit mb-2 bg-amber-100 text-amber-700">Risk Analysis</Badge>
          <CardTitle className="text-lg">Assumptions & Risk Factors</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Adjustment Factors */}
            <div>
              <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4 text-amber-600" />
                Dampening Factors Applied
              </h4>
              <div className="space-y-2">
                {results.combined_adjustment_factor && (
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-sm text-slate-600">Combined Adjustment</span>
                    <span className="font-medium">{(results.combined_adjustment_factor * 100).toFixed(1)}%</span>
                  </div>
                )}
                {results.dampening && (
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-sm text-slate-600">Execution Dampening</span>
                    <span className="font-medium">{(results.dampening * 100).toFixed(1)}%</span>
                  </div>
                )}
                {results.maturity_factor && (
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-sm text-slate-600">Maturity Factor</span>
                    <span className="font-medium">{(results.maturity_factor * 100).toFixed(1)}%</span>
                  </div>
                )}
                {results.transfer_rate?.transfer_rate_percent != null && (
                  <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                    <span className="text-sm text-slate-600">Capability Transfer Rate</span>
                    <span className="font-medium">{(results.transfer_rate.transfer_rate_percent || 0).toFixed(0)}%</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Key Assumptions */}
            <div>
              <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                Key Assumptions
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  Results assume successful program rollout and adoption
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  Payback period assumes consistent implementation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  KPI uplift may be affected by external market factors
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  Financial impact based on provided baseline values
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Page 5: Full AI Narrative (Comprehensive Mode Only) */}
      {contentLevel === 'comprehensive' && (
        <Card data-testid="comprehensive-narrative-card" className={`border-2 ${effectiveNarrative ? 'border-purple-200' : 'border-dashed border-purple-200 bg-purple-50/30'}`}>
          <CardHeader className={effectiveNarrative ? "bg-gradient-to-r from-purple-50 to-indigo-50 border-b" : ""}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Sparkles className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <Badge className="mb-2 bg-purple-100 text-purple-700">AI-Generated Analysis</Badge>
                  <CardTitle className="text-lg">Full Narrative Report</CardTitle>
                  <CardDescription>
                    {effectiveNarrative 
                      ? "Comprehensive AI-powered analysis of your learning investment" 
                      : "Generate an AI narrative for this scenario"}
                  </CardDescription>
                </div>
              </div>
              
              {/* Generate/Regenerate Button */}
              <Button
                onClick={handleGenerateNarrative}
                disabled={isGeneratingNarrative}
                variant={effectiveNarrative ? "outline" : "default"}
                className={effectiveNarrative 
                  ? "border-purple-300 text-purple-700 hover:bg-purple-50" 
                  : "bg-purple-600 hover:bg-purple-700 text-white"}
              >
                {isGeneratingNarrative ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : effectiveNarrative ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Regenerate
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Narrative
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            {effectiveNarrative ? (
              <NarrativeSections narrative={effectiveNarrative} />
            ) : (
              <div className="text-center py-8">
                <BookOpen className="h-12 w-12 text-purple-700 mx-auto mb-4" />
                <p className="text-sm text-slate-500 max-w-md mx-auto">
                  Click "Generate Narrative" to create an AI-powered analysis that explains your results 
                  with an executive summary, technical breakdown, risk analysis, and strategic recommendations.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Page 6: Recommendations (if comprehensive or has warning_details/narrative) */}
      {(contentLevel === 'comprehensive' || effectiveNarrative || results.warning_details) && (
        <Card data-testid="recommendations-card">
          <CardHeader>
            <Badge className="w-fit mb-2 bg-emerald-100 text-emerald-700">Strategic Recommendations</Badge>
            <CardTitle className="text-lg">Next Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg border border-emerald-200">
                <h4 className="font-medium text-emerald-800 mb-2 flex items-center gap-2">
                  <Lightbulb className="h-4 w-4" />
                  Quick Wins
                </h4>
                <ul className="text-sm text-emerald-700 space-y-1">
                  <li>• Pilot with a subset of learners to validate assumptions</li>
                  <li>• Secure executive sponsorship for adoption support</li>
                  <li>• Define clear success metrics and checkpoints</li>
                </ul>
              </div>
              
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Medium-Term Priorities
                </h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Build feedback loops to track leading indicators</li>
                  <li>• Document methodology for stakeholder buy-in</li>
                  <li>• Establish measurement cadence for ongoing tracking</li>
                </ul>
              </div>
            </div>
            
            {/* Attention Required - Using warning_details from Analysis Notes */}
            {results.warning_details && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Analysis Notes
                </h4>
                
                {/* Handle both array and object formats */}
                {(Array.isArray(results.warning_details) ? results.warning_details : [results.warning_details]).map((detail, index) => (
                  <div key={detail?.name || detail?.headline || `intervention-${index}`} className={index > 0 ? "mt-4 pt-4 border-t border-amber-200" : ""}>
                    {/* Headline from warnings array or from detail.headline */}
                    {(results.warnings?.[index] || detail.headline) && (
                      <h5 className="font-semibold text-amber-900 mb-2">
                        {results.warnings?.[index] || detail.headline}
                      </h5>
                    )}
                    
                    {/* Explanation */}
                    {detail.explanation && (
                      <p className="text-sm text-amber-700 mb-4">
                        {detail.explanation}
                      </p>
                    )}
                    
                    {/* ROI Ranges Table - Left justified (handle both typical_ranges and roi_ranges) */}
                    {(detail.typical_ranges || detail.roi_ranges) && (
                      <div className="mb-4 p-3 bg-white/50 rounded border border-amber-200">
                        <p className="text-sm font-medium text-amber-800 mb-2 underline">Typical ROI Ranges:</p>
                        <div className="space-y-1 text-sm">
                          {Object.entries(detail.typical_ranges || detail.roi_ranges).map(([key, val]) => (
                            <div key={key} className="flex">
                              <span className="capitalize text-amber-600 w-28">{key}:</span>
                              <span className="text-amber-800">{val}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Recommended Actions (handle both guidance and recommendations) */}
                    {((detail.guidance && detail.guidance.length > 0) || (detail.recommendations && detail.recommendations.length > 0)) && (
                      <div>
                        <p className="text-sm font-medium text-amber-800 mb-2 underline">Recommended Actions:</p>
                        <ul className="text-sm text-amber-700 space-y-1">
                          {(detail.guidance || detail.recommendations).map((rec, i) => (
                            <li key={`rec-${rec.slice(0, 25)}-${i}`} className="flex items-start gap-2">
                              <span className="text-amber-500 mt-0.5">→</span>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Fallback to simple warnings if no warning_details */}
            {!results.warning_details && results.warnings && results.warnings.length > 0 && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Attention Required
                </h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  {results.warnings.slice(0, 3).map((warning, i) => (
                    <li key={`warn-${warning.slice(0, 25)}-${i}`}>• {warning}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Footer */}
      <div className="text-center text-sm text-slate-500 py-4 border-t">
        <p>Generated by Seertech • {new Date().toLocaleDateString()}</p>
        {userLogo && <p className="mt-1">Custom branding enabled</p>}
      </div>
    </div>
    </PageShell>
  );
};

export default BoardReadyPage;
